import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetSavedPropertiesQuery,
  useGetUserFavoritesQuery,
} from '~/redux/api/userApiSlice';
import FavoritesCardList from '~activities/pages/Favorites/FavoritesCardList';
import Pagination from '~/components/Pagination';
import FavoritesLoader from '~/modules/activities/pages/Favorites/FavoritesLoader';
import EmptySate from '~activities/pages/Favorites/EmptySate';
import ErrorSate from '~activities/pages/Favorites/ErrorSate';
import FavoritesHeader from '~activities/pages/Favorites/FavoritesHeader';
import FavoritesFilter from '~activities/pages/Favorites/FavoritesFilter';
import {
  SavedPropertyFilterTabType,
  SavedPropertyFilterType,
  SavedPropertyItemType,
} from '~/types/activitiesTypes';
import { useParams, useSearchParams } from 'react-router-dom';
import DeleteFavoritesModal from '~activities/pages/Favorites/DeleteFavoritesModal';

const PAGE_LIMIT = 20;

const SavedProperties = ({
  isSheared = false,
  showNotes = false,
  setTotalListing,
}: {
  isSheared?: boolean;
  showNotes?: boolean;
  setTotalListing?: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation('app');
  const [activeTab, setActiveTab] =
    useState<SavedPropertyFilterTabType>('favorites');
  const [filterState, setFilterState] = useState<SavedPropertyFilterType>({
    sort: '',
    direction: '',
    page: 1,
    limit: PAGE_LIMIT,
    includeNotes: activeTab === 'notes',
    sortBy: '',
  });
  const { id } = useParams();
  const searchParams = useSearchParams();
  const _expiration = searchParams[0].get('_expiration');
  const _hash = searchParams[0].get('_hash');
  const [listings, setListings] = useState<SavedPropertyItemType[]>([]);
  const [sharedFavoritesUrl, setSharedFavoritesUrl] = useState<string | null>(
    null,
  );
  const [sharedNotesUrl, setSharedNotesUrl] = useState<string | null>(null);

  const {
    data: listingsWithFavoritesData,
    isFetching: isFavoritesFetching,
    isError: isFavoritesError,
    refetch: refetchFavorites,
    isSuccess: isFavoritesSuccess,
  } = useGetSavedPropertiesQuery(
    {
      ...filterState,
      includeNotes: false,
    },
    {
      skip: isSheared,
    },
  );
  const {
    data: listingsWithNoteData,
    isFetching: isNotesFetching,
    isError: isNotesError,
    refetch: refetchNotes,
    isSuccess: isNotesSuccess,
  } = useGetSavedPropertiesQuery(
    {
      ...filterState,
      includeNotes: true,
    },
    {
      skip: isSheared,
    },
  );

  const {
    data: listingsUserData,
    isFetching: isListingsUserFetching,
    isError: isListingsUserError,
    refetch: refetchListingsUser,
    isSuccess: isListingsUserSuccess,
  } = useGetUserFavoritesQuery(
    {
      query: {
        ...filterState,
        includeNotes: false,
        _expiration: _expiration || '',
        _hash: _hash || '',
      },
      userId: id || '',
    },
    {
      skip: !isSheared,
    },
  );

  const {
    data: listingsUserNotesData,
    isFetching: isListingsUserNotesFetching,
    isError: isListingsUserNotesError,
    refetch: refetchListingsNotesUser,
    isSuccess: isListingsUserNotesSuccess,
  } = useGetUserFavoritesQuery(
    {
      query: {
        ...filterState,
        includeNotes: true,
        _expiration: _expiration || '',
        _hash: _hash || '',
      },
      userId: id || '',
    },
    {
      skip: !isSheared || !showNotes,
    },
  );

  const isFetching =
    isFavoritesFetching ||
    isNotesFetching ||
    isListingsUserFetching ||
    isListingsUserNotesFetching;
  const isError =
    isFavoritesError ||
    isNotesError ||
    isListingsUserError ||
    isListingsUserNotesError;
  const refetch = () => {
    if (isSheared) {
      refetchListingsUser();
      refetchListingsNotesUser;
    } else {
      refetchFavorites();
      refetchNotes();
    }
  };

  const favorites = useMemo(() => {
    if (!isFavoritesSuccess || !isNotesSuccess) return [];

    if (activeTab === 'favorites')
      return listingsWithFavoritesData?.data?.data || [];

    return (
      listingsWithNoteData?.data?.data?.filter((item) => item.note !== '') || []
    );
  }, [
    activeTab,
    isFavoritesSuccess,
    isNotesSuccess,
    listingsWithFavoritesData,
    listingsWithNoteData,
    isListingsUserSuccess,
  ]);
  const sharedFavorites = useMemo(() => {
    if (activeTab === 'favorites') return listingsUserData?.data || [];
    else
      return (
        listingsUserNotesData?.data?.filter((item) => item.note !== '') || []
      );
  }, [
    activeTab,
    isListingsUserSuccess,
    isListingsUserNotesSuccess,
    listingsUserData,
    listingsUserNotesData,
  ]);

  const [favoritesCount, setFavoritesCount] = useState(0);
  const [notesCount, setNotesCount] = useState(0);

  const tabs: {
    key: SavedPropertyFilterTabType;
    label: string;
    count: number;
    hide?: boolean;
  }[] = [
    {
      key: 'favorites',
      label: t('savedPropertiesFavoritesTab'),
      count: favoritesCount,
      hide: false,
    },
    {
      key: 'notes',
      label: t('savedPropertiesNotesTab'),
      count: notesCount,
      hide: !showNotes && isSheared,
    },
  ];

  const activeTabCount =
    activeTab === 'favorites' ? favoritesCount : notesCount;

  const showPagination =
    activeTab === 'favorites'
      ? favoritesCount > PAGE_LIMIT
      : notesCount > PAGE_LIMIT;

  useEffect(() => {
    refetch();
  }, [filterState, activeTab]);

  useEffect(() => {
    if (!isFavoritesSuccess || !isNotesSuccess) return;

    setSharedFavoritesUrl(
      listingsWithFavoritesData?.headers.get('x-shared-favorites-url') || null,
    );
    setSharedNotesUrl(
      listingsWithNoteData?.headers.get('x-shared-favorites-notes-url') || null,
    );
  }, [
    isFavoritesSuccess,
    isNotesSuccess,
    listingsWithFavoritesData,
    listingsWithNoteData,
  ]);

  useEffect(() => {
    if (isSheared) {
      setFavoritesCount(listingsUserData?.paginate?.totalCount || 0);
      setNotesCount(listingsUserNotesData?.paginate?.totalCount || 0);
      if (setTotalListing)
        setTotalListing(listingsUserData?.paginate?.totalCount || 0);
    } else {
      setFavoritesCount(
        listingsWithFavoritesData?.data?.paginate?.totalCount || 0,
      );
      setNotesCount(listingsWithNoteData?.data?.paginate?.totalCount || 0);
    }
  }, [listingsWithFavoritesData, listingsWithNoteData, listingsUserData]);

  useEffect(() => {
    setListings(isSheared ? sharedFavorites : favorites);
  }, [favorites, sharedFavorites]);

  return (
    <section className="flex w-full flex-col gap-3x md:gap-4x flex-1">
      {!isSheared ? <FavoritesHeader favoritesCount={favoritesCount} /> : null}
      {favoritesCount ? (
        <FavoritesFilter
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          filterState={filterState}
          setFilterState={setFilterState}
        />
      ) : null}
      <div className="flex w-full flex-col gap-2x md:gap-4x px-2x md:px-0">
        {isFetching ? <FavoritesLoader /> : null}
        {!isFetching && isError ? <ErrorSate /> : null}

        {!isFetching && !isError && !activeTabCount ? (
          <EmptySate variant={activeTab === 'notes' ? 'notes' : 'favorites'} />
        ) : null}

        {!isFetching && !isError && listings.length ? (
          <FavoritesCardList
            listings={listings}
            setListings={setListings}
            setFavoritesCount={setFavoritesCount}
            setNotesCount={setNotesCount}
            sharedFavoritesUrl={sharedFavoritesUrl}
            sharedNotesUrl={sharedNotesUrl}
            isSheared={isSheared}
            activeTab={activeTab}
          />
        ) : null}

        {showPagination ? (
          <Pagination
            currentPage={filterState.page}
            totalCount={activeTab === 'favorites' ? favoritesCount : notesCount}
            limit={PAGE_LIMIT}
            className="mt-2x"
            onPageChange={(nextPage) => {
              setFilterState((prev) => ({ ...prev, page: nextPage }));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            showEdgeNavigation={false}
          />
        ) : null}
      </div>
      <DeleteFavoritesModal
        setFavoritesCount={setFavoritesCount}
        setListings={setListings}
        setNotesCount={setNotesCount}
      />
    </section>
  );
};

export default SavedProperties;
